/**
 * CoSingery — **登入頁底下「一格」：自動判斷你打嘅係 6 位數碼抑或邀請碼**（`decisions/045` 0020d）
 *
 * 產品負責人 2026-09-25：「**why need so many words and explaination in the login page, UX so bad**, just
 *   keep the login box, the two button for email login and the one with invitation code, then bottom
 *   **either 6 digit code or invitation no. should auto detect which one appear for input**」
 *
 * 判斷規則（零額外文字）：
 *   · **純數字**（≤6 位）→ **6 位數碼**；夠 6 位就**自動登入**（唔需要再撳任何掣）
 *   · **有英文字母**（`INV-XXXX-XXXX-XXXX`）→ **邀請碼**；由上面〔用邀請碼登入〕掣負責送出
 *   · 空白 → 跟上面兩粒掣嘅意圖（撳咗〔用邀請碼登入〕就當邀請碼，否則當數碼）
 *
 * ⚠️ 紅線：
 *   · 前端**唔判斷邀請碼對錯**（只有 server 知 `sha256`）—— 呢度只判斷「係邊一種」
 *   · 成功登記一律**整頁重新載入**（身份唯一來源係 server cookie；唔准自己改 state 扮登入）
 *   · 失敗照實講（`code_invalid`／`email_invalid`／`rate_limited` 分開講）
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

export type LoginFieldKind = 'empty' | 'code' | 'invite';

/** 6 位一次性數碼長度（同 server `AUTH_CODE_DIGITS` 對齊） */
export const LOGIN_CODE_LENGTH = 6;
/** 邀請碼最長（`INV-XXXX-XXXX-XXXX` ＝ 18 字） */
const INVITE_MAX_LEN = 18;

/** ⭐ **自動判斷**：有字母 → 邀請碼；純數字 → 6 位數碼（零模糊：邀請碼字母表一定含字母） */
export function detectLoginField(raw: string): LoginFieldKind {
  const s = raw.trim();
  if (!s) return 'empty';
  return /[A-Za-z]/.test(s) ? 'invite' : 'code';
}

export default function LoginCodeOrInvite({
  email,
  value,
  onChange,
  wantInvite,
  focusSignal,
  onSignedIn,
  inputStyle,
}: {
  email: string;
  value: string;
  onChange: (v: string) => void;
  /** 用戶撳咗〔用邀請碼登入〕／由 `?invite=` 帶入 → 空白時當邀請碼 */
  wantInvite?: boolean;
  /** 每次「寄出信」或「撳〔用邀請碼登入〕但未填」就 +1 → 自動捲入畫面 ＋ 對焦 */
  focusSignal?: number;
  onSignedIn: () => void;
  inputStyle: React.CSSProperties;
}) {
  const { t } = useTranslation();
  const { verifyCode } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** 已經試過嘅值（唔准同一個碼重複自動送；亦防止 effect 重跑撞兩次） */
  const triedRef = useRef('');
  const digits = value.replace(/[^0-9]/g, '');
  const detected = detectLoginField(value);
  /** 空白 → 跟掣嘅意圖；有內容 → 內容話事 */
  const kind: LoginFieldKind = detected === 'empty' ? (wantInvite ? 'invite' : 'code') : detected;

  /** ⭐ 捲入畫面 ＋ 對焦（寄完信／撳咗邀請掣）—— 唔准要用戶自己搵 */
  useEffect(() => {
    if (!focusSignal) return;
    inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    inputRef.current?.focus();
  }, [focusSignal]);

  /** ⭐ 夠 6 位數字 → **自動登入**（產品負責人：唔要再撳掣） */
  useEffect(() => {
    if (kind !== 'code' || digits.length !== LOGIN_CODE_LENGTH) return;
    if (triedRef.current === value) return;
    if (!email.trim()) {
      setError(t('login_need_email_short'));
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        triedRef.current = value;
        setError(null);
        setBusy(true);
        const res = await verifyCode(email.trim(), digits);
        setBusy(false);
        if (res.ok) {
          onSignedIn();
          return;
        }
        setError(
          res.code === 'network_error'
            ? t('login_code_error_network')
            : res.code === 'email_invalid'
              ? t('login_error_email_invalid')
              : res.code === 'rate_limited'
                ? t('login_error_too_many')
                : t('login_code_error_invalid'),
        );
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [value, digits, kind, email, onSignedIn, t, verifyCode]);

  const label = kind === 'invite' ? t('login_invite_label') : t('login_code_label');

  return (
    <div style={{ display: 'grid', gap: '6px' }}>
      <label htmlFor="login-field" style={{ fontSize: 'var(--font-size-body, 19px)' }}>
        {label}
      </label>
      <input
        ref={inputRef}
        id="login-field"
        type="text"
        inputMode={kind === 'invite' ? 'text' : 'numeric'}
        autoComplete={kind === 'invite' ? 'off' : 'one-time-code'}
        autoCapitalize={kind === 'invite' ? 'characters' : 'off'}
        maxLength={kind === 'invite' ? INVITE_MAX_LEN : LOGIN_CODE_LENGTH}
        data-testid="login-field"
        data-kind={kind}
        placeholder={kind === 'invite' ? t('login_invite_placeholder') : t('login_code_placeholder')}
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          /** 數碼模式只收數字（≤6）；邀請碼模式收大寫（方便貼 INV-… 或打 6 位數碼轉模式） */
          onChange(kind === 'invite' ? raw.toUpperCase().slice(0, INVITE_MAX_LEN) : raw.replace(/[^0-9]/g, '').slice(0, LOGIN_CODE_LENGTH));
          setError(null);
          triedRef.current = '';
        }}
        style={{ ...inputStyle, letterSpacing: kind === 'code' ? '4px' : undefined, textAlign: kind === 'code' ? 'center' : undefined }}
      />
      {busy ? (
        <p className="footnote" style={{ margin: 0 }} data-testid="login-field-busy">
          {t('login_code_busy')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" style={{ margin: 0, color: 'var(--color-danger, #b3261e)' }} data-testid="login-field-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
